import { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft, Shield, Trash2, Settings, Eye, EyeOff, BadgeCheck, Users,
  Play, Square, Wifi, WifiOff, Loader2, ChevronDown, ChevronUp, Clock,
} from 'lucide-react';
import {
  useVantageAccounts, useCreateVantageAccount, useDeleteVantageAccount,
  useVantageAccountProviders, useUpdateProvider, useProviderTrades,
  useProviderSignals, useCreateSignal, useUpdateSignal,
  useBridgeStatus, useStartBridge, useStopBridge, useCopyPrices,
  type CopyProvider, type CopySignal,
} from '@/hooks/use-copy-trade';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { formatPnl, pnlColor, formatPrice, getClientContractSize } from '@/lib/format';

export default function CopyAdminPage() {
  const { data: accounts, isLoading } = useVantageAccounts();
  const { data: bridgeStatus } = useBridgeStatus();
  const startBridge = useStartBridge();
  const stopBridge = useStopBridge();
  const deleteMutation = useDeleteVantageAccount();
  const [showSetup, setShowSetup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const account = accounts?.[0] ?? null;
  const connected = bridgeStatus?.running === true;
  const processAlive = bridgeStatus?.processAlive === true;

  // Clear connecting state once bridge is connected
  if (connected && connecting) setConnecting(false);

  if (isLoading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/copy-trade" className="text-xs text-ink-3 hover:text-orange flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to Copy Trade
        </Link>
        <h1 className="text-xl font-bold text-ink flex items-center gap-2">
          <Shield size={22} className="text-orange" /> Copy Trade Admin
        </h1>
      </div>

      {!account ? (
        <div className="glass-sm rounded-lg p-8 text-center border border-border">
          <Wifi size={36} className="text-ink-4 mx-auto mb-3" />
          <p className="text-sm font-semibold text-ink">Connect your Vantage account</p>
          <p className="text-xs text-ink-4 mt-1 mb-4 max-w-md mx-auto">
            Link your Vantage copy-trading account to discover and mirror signal providers
          </p>
          <Button size="sm" onClick={() => setShowSetup(true)}>Connect Vantage Account</Button>
        </div>
      ) : (
        <>
          {/* Bridge status */}
          <div className={`glass-sm rounded-lg p-4 border ${connected ? 'border-green/30' : 'border-border'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {connected ? <Wifi size={18} className="text-green" /> : <WifiOff size={18} className="text-ink-4" />}
                <div>
                  <p className="text-sm font-bold text-ink">{account.name} — {connected ? 'Connected' : processAlive ? 'Starting...' : 'Disconnected'}</p>
                  <p className="text-[10px] text-ink-4">
                    {connected
                      ? `${bridgeStatus!.providersCount} provider(s) syncing${bridgeStatus!.lastSync ? ` · last sync ${new Date(bridgeStatus!.lastSync).toLocaleTimeString()}` : ''}`
                      : 'Click Connect to start syncing'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {connected ? (
                  <Button variant="danger" size="sm" onClick={() => stopBridge.mutate()} disabled={stopBridge.isPending}>
                    {stopBridge.isPending ? <Spinner size="sm" /> : <><Square size={12} className="mr-1" /> Disconnect</>}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => { setConnecting(true); startBridge.mutate(); }} disabled={connecting}>
                    {connecting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Play size={12} className="mr-1" />}
                    {connecting ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
                <button onClick={() => setShowDeleteConfirm(true)} className="text-ink-4 hover:text-red transition-colors cursor-pointer p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Providers + Signals */}
          <ProvidersSection accountId={account.id} connected={connected} />
        </>
      )}

      <SetupAccountModal open={showSetup} onClose={() => setShowSetup(false)} />
      <Modal open={showDeleteConfirm} title="Remove Vantage Account" onClose={() => setShowDeleteConfirm(false)}>
        <p className="text-sm text-ink-3 mb-4">Remove <strong>{account?.name}</strong>?</p>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)} className="flex-1">Cancel</Button>
          <Button variant="danger" onClick={() => {
            if (connected) stopBridge.mutate();
            deleteMutation.mutate(account!.id, { onSuccess: () => setShowDeleteConfirm(false) });
          }} disabled={deleteMutation.isPending} className="flex-1">
            {deleteMutation.isPending ? <Spinner size="sm" /> : 'Remove'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ─── PROVIDERS + SIGNALS ──────────────────────────────────────────────

function ProvidersSection({ accountId, connected }: { accountId: string; connected: boolean }) {
  const { data: providers, isLoading } = useVantageAccountProviders(accountId);

  if (isLoading) return <div className="flex items-center justify-center py-8"><Spinner size="sm" /></div>;
  if (!providers || providers.length === 0) {
    return (
      <div className="glass-sm rounded-lg p-6 text-center border border-border">
        <Users size={28} className="text-ink-4 mx-auto mb-2" />
        <p className="text-sm font-semibold text-ink-3">No providers discovered yet</p>
        <p className="text-xs text-ink-4 mt-1">{connected ? 'Providers appear as the bridge syncs' : 'Connect to discover providers'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-bold text-ink">Signal Providers ({providers.length})</h2>
      {providers.map(provider => (
        <ProviderWithSignals key={provider.id} provider={provider} />
      ))}
    </div>
  );
}

function ProviderWithSignals({ provider }: { provider: CopyProvider }) {
  const { data: signals } = useProviderSignals(provider.id);
  const [showConfig, setShowConfig] = useState(false);
  const [showTrades, setShowTrades] = useState(false);
  const { data: tradesData } = useProviderTrades(provider.id, {
    status: 'OPEN',
    limit: 50,
  });
  const { data: livePrices } = useCopyPrices();

  const trades = tradesData?.trades ?? [];

  return (
    <div className="glass-sm rounded-lg border border-glass-border overflow-hidden">
      {/* Provider header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <Link to={`/copy-trade/admin/provider/${provider.id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            {provider.avatar_url ? (
              <img src={provider.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                <Users size={16} className="text-orange" />
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-ink">{provider.display_name}</p>
              <div className="flex items-center gap-3 text-[10px] text-ink-4 mt-0.5">
                <span>{provider.symbol}</span>
                <span>{provider.total_trades} trades</span>
                <span className={pnlColor(provider.total_pnl)}>{formatPnl(provider.total_pnl)}</span>
                {provider.win_rate > 0 && <span>{provider.win_rate.toFixed(0)}% win</span>}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTrades(!showTrades)}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors cursor-pointer flex items-center gap-1 ${
                showTrades ? 'bg-orange/10 text-orange' : 'bg-surface text-ink-3 hover:text-ink'
              }`}>
              <Clock size={10} /> Open Positions {showTrades ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
            <button onClick={() => setShowConfig(true)} className="text-ink-4 hover:text-orange transition-colors cursor-pointer p-1">
              <Settings size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Open positions (expandable) */}
      {showTrades && (
        <div className="border-t border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-ink flex items-center gap-1.5">
              <Clock size={12} className="text-orange" /> Open Positions
            </p>
          </div>
          {trades.length === 0 ? (
            <p className="text-xs text-ink-4 text-center py-4">No open positions</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-border text-ink-4">
                    <th className="text-left px-2 py-1.5 font-medium">Symbol</th>
                    <th className="text-left px-2 py-1.5 font-medium">Type</th>
                    <th className="text-right px-2 py-1.5 font-medium">Vol</th>
                    <th className="text-right px-2 py-1.5 font-medium">Open</th>
                    <th className="text-right px-2 py-1.5 font-medium">Close</th>
                    <th className="text-right px-2 py-1.5 font-medium">P&L</th>
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-left px-2 py-1.5 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map(t => {
                    let pnl = t.gross_pnl;
                    let closePrice: number | null = t.close_price;
                    if (t.status === 'OPEN' && livePrices) {
                      const p = livePrices[t.symbol];
                      if (p) {
                        closePrice = t.type === 'BUY' ? p.bid : p.ask;
                        const diff = t.type === 'BUY' ? closePrice - t.open_price : t.open_price - closePrice;
                        pnl = parseFloat((diff * t.volume * getClientContractSize(t.symbol, livePrices || undefined)).toFixed(2));
                      }
                    }
                    const date = new Date(t.opened_at);
                    return (
                      <tr key={t.id} className="border-b border-border/30 hover:bg-surface/30">
                        <td className="px-2 py-1.5 font-semibold text-ink">{t.symbol}</td>
                        <td className="px-2 py-1.5">
                          <Badge color={t.type === 'BUY' ? 'green' : 'red'}>{t.type}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-ink-3">
                          {t.volume < 0.01 ? t.volume.toFixed(4) : t.volume.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-ink-3">{formatPrice(t.open_price, t.symbol)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-ink-3">
                          {closePrice != null ? formatPrice(closePrice, t.symbol) : '—'}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-mono font-semibold ${pnlColor(pnl)}`}>
                          {pnl !== 0 ? formatPnl(pnl) : '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge color={t.status === 'OPEN' ? 'blue' : 'default'}>{t.status}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-ink-4">
                          {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Signals list */}
      {signals && signals.length > 0 && (
        <div className="border-t border-border divide-y divide-border">
          {signals.map(signal => (
            <SignalRow key={signal.id} signal={signal} />
          ))}
        </div>
      )}

      <ProviderConfigModal open={showConfig} provider={provider} onClose={() => setShowConfig(false)} />
    </div>
  );
}

function SignalRow({ signal }: { signal: CopySignal }) {
  const symbolDisplay = signal.symbol_filter?.join(', ') || 'All Symbols';
  const avatarUrl = signal.avatar_url || dicebearAvatar(signal.name);

  return (
    <Link
      to={`/copy-trade/${signal.id}`}
      className="block px-4 py-3 hover:bg-surface/30 transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover bg-surface flex-shrink-0" />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-ink">{signal.name}</span>
              {signal.is_verified && <BadgeCheck size={12} className="text-blue-500" />}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-ink-4 mt-0.5">
              <span>{symbolDisplay}</span>
              {signal.default_lot_size && <span>{signal.default_lot_size} lot</span>}
              {signal.default_sl_pct && <span>{signal.default_sl_pct}% SL</span>}
              <span>{signal.total_trades} trades</span>
              <span className={pnlColor(signal.total_pnl)}>{formatPnl(signal.total_pnl)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {signal.is_visible ? (
            <Badge color="green"><Eye size={9} className="mr-0.5" /> Published</Badge>
          ) : (
            <Badge color="default"><EyeOff size={9} className="mr-0.5" /> Hidden</Badge>
          )}
        </div>
      </div>
    </Link>
  );
}

function dicebearAvatar(seed: string): string {
  return `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`;
}

// ─── MODALS ───────────────────────────────────────────────────────────

export function CreateSignalModal({ open, provider, onClose }: { open: boolean; provider: CopyProvider; onClose: () => void }) {
  const createSignal = useCreateSignal();
  const [form, setForm] = useState({
    name: '',
    description: '',
    symbol_filter: '',
    default_lot_size: '0.01',
    default_max_positions: '10',
    default_sl_pct: '20',
    default_tp_pct: '',
  });
  const update = (f: string, v: string) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Modal open={open} title={`Create Signal: ${provider.display_name}`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Signal Name</label>
          <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="SATORI Conservative" />
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Description</label>
          <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={2}
            className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange resize-none"
            placeholder="Conservative gold scalping with tight risk..." />
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Symbol Filter</label>
          <Input value={form.symbol_filter} onChange={e => update('symbol_filter', e.target.value)} placeholder="XAUUSD+" />
          <p className="text-[10px] text-ink-4 mt-1">Comma-separated. Leave empty for all symbols.</p>
        </div>
        <div className="border-t border-border pt-3">
          <p className="text-xs font-bold text-ink mb-2">Risk Defaults (suggestions for subscribers)</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-ink-3 block mb-1">Lot Size</label>
              <Input type="number" value={form.default_lot_size} onChange={e => update('default_lot_size', e.target.value)} min="0.01" step="0.01" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-ink-3 block mb-1">Max Positions</label>
              <Input type="number" value={form.default_max_positions} onChange={e => update('default_max_positions', e.target.value)} min="1" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-ink-3 block mb-1">Stop Loss %</label>
              <Input type="number" value={form.default_sl_pct} onChange={e => update('default_sl_pct', e.target.value)} min="5" max="95" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-ink-3 block mb-1">Take Profit %</label>
              <Input type="number" value={form.default_tp_pct} onChange={e => update('default_tp_pct', e.target.value)} min="5" max="95" placeholder="Optional" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={() => {
            if (!form.name) return;
            const symbols = form.symbol_filter.split(',').map(s => s.trim()).filter(Boolean);
            createSignal.mutate({
              providerId: provider.id,
              name: form.name,
              description: form.description || undefined,
              symbol_filter: symbols.length > 0 ? symbols : undefined,
              default_lot_size: parseFloat(form.default_lot_size) || undefined,
              default_max_positions: parseInt(form.default_max_positions) || undefined,
              default_sl_pct: parseFloat(form.default_sl_pct) || undefined,
              default_tp_pct: parseFloat(form.default_tp_pct) || undefined,
            }, { onSuccess: onClose });
          }} disabled={!form.name || createSignal.isPending} className="flex-1">
            {createSignal.isPending ? <Spinner size="sm" /> : 'Create Signal'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function EditSignalModal({ open, signal, onClose }: { open: boolean; signal: CopySignal; onClose: () => void }) {
  const updateSignal = useUpdateSignal();
  const [form, setForm] = useState({
    name: signal.name,
    description: signal.description || '',
    symbol_filter: signal.symbol_filter?.join(', ') || '',
    default_lot_size: String(signal.default_lot_size || ''),
    default_max_positions: String(signal.default_max_positions || 10),
    default_sl_pct: String(signal.default_sl_pct || ''),
    default_tp_pct: String(signal.default_tp_pct || ''),
  });
  const update = (f: string, v: string) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Modal open={open} title={`Edit: ${signal.name}`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Signal Name</label>
          <Input value={form.name} onChange={e => update('name', e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Description</label>
          <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={2}
            className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange resize-none" />
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Symbol Filter</label>
          <Input value={form.symbol_filter} onChange={e => update('symbol_filter', e.target.value)} placeholder="XAUUSD+" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[10px] font-semibold text-ink-3 block mb-1">Lot Size</label>
            <Input type="number" value={form.default_lot_size} onChange={e => update('default_lot_size', e.target.value)} /></div>
          <div><label className="text-[10px] font-semibold text-ink-3 block mb-1">Max Positions</label>
            <Input type="number" value={form.default_max_positions} onChange={e => update('default_max_positions', e.target.value)} /></div>
          <div><label className="text-[10px] font-semibold text-ink-3 block mb-1">Stop Loss %</label>
            <Input type="number" value={form.default_sl_pct} onChange={e => update('default_sl_pct', e.target.value)} /></div>
          <div><label className="text-[10px] font-semibold text-ink-3 block mb-1">Take Profit %</label>
            <Input type="number" value={form.default_tp_pct} onChange={e => update('default_tp_pct', e.target.value)} placeholder="Optional" /></div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={() => {
            const symbols = form.symbol_filter.split(',').map(s => s.trim()).filter(Boolean);
            updateSignal.mutate({
              id: signal.id,
              data: {
                name: form.name,
                description: form.description || null,
                symbol_filter: symbols.length > 0 ? symbols : null,
                default_lot_size: parseFloat(form.default_lot_size) || null,
                default_max_positions: parseInt(form.default_max_positions) || 10,
                default_sl_pct: parseFloat(form.default_sl_pct) || null,
                default_tp_pct: parseFloat(form.default_tp_pct) || null,
              } as any,
            }, { onSuccess: onClose });
          }} disabled={updateSignal.isPending} className="flex-1">
            {updateSignal.isPending ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ProviderConfigModal({ open, provider, onClose }: { open: boolean; provider: CopyProvider; onClose: () => void }) {
  const updateProvider = useUpdateProvider();
  const [form, setForm] = useState({ display_name: provider.display_name, description: provider.description || '' });
  return (
    <Modal open={open} title={`Configure: ${provider.display_name}`} onClose={onClose}>
      <div className="space-y-3">
        <div><label className="text-xs font-semibold text-ink-3 block mb-1">Display Name</label>
          <Input value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} /></div>
        <div><label className="text-xs font-semibold text-ink-3 block mb-1">Description</label>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
            className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange resize-none" /></div>
        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={() => updateProvider.mutate({ id: provider.id, data: form }, { onSuccess: onClose })}
            disabled={updateProvider.isPending} className="flex-1">
            {updateProvider.isPending ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SetupAccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createMutation = useCreateVantageAccount();
  const [form, setForm] = useState({ name: 'Vantage Main', account_id: '', server_account_id: '', email: '', password: '', sign_secret: '1457929724SJHKJ1590405016hytech' });
  const update = (f: string, v: string) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Modal open={open} title="Connect Vantage Account" onClose={onClose}>
      <div className="space-y-3">
        <div><label className="text-xs font-semibold text-ink-3 block mb-1">Display Name</label>
          <Input value={form.name} onChange={e => update('name', e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-ink-3 block mb-1">Account ID</label>
            <Input value={form.account_id} onChange={e => update('account_id', e.target.value)} placeholder="775861" /></div>
          <div><label className="text-xs font-semibold text-ink-3 block mb-1">Server Account ID</label>
            <Input value={form.server_account_id} onChange={e => update('server_account_id', e.target.value)} /></div>
        </div>
        <div><label className="text-xs font-semibold text-ink-3 block mb-1">Vantage Email</label>
          <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} /></div>
        <div><label className="text-xs font-semibold text-ink-3 block mb-1">Vantage Password</label>
          <Input type="password" value={form.password} onChange={e => update('password', e.target.value)} /></div>
        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={() => createMutation.mutate(form, { onSuccess: onClose })}
            disabled={!form.account_id || !form.email || !form.password || createMutation.isPending} className="flex-1">
            {createMutation.isPending ? <Spinner size="sm" /> : 'Connect'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
